import {
  headingFlags,
  type Sentence,
  splitSentences,
  wordTokens,
} from "@/lib/tool-kit/writing-analysis/text";

/**
 * How each sentence opens: its first word, its first two words, and what kind of
 * opening it is — a transition, an introductory clause, an introductory phrase, or the
 * subject itself.
 *
 * Two patterns are flagged, both about rhythm rather than correctness: three or more
 * consecutive sentences that open with the same word, and heavy use of the five
 * openers that tend to pile up in academic drafts — The, This, It, There and I. "The"
 * is flagged at 30% of sentences because it is the most common opener in English; the
 * other four at 20%. Either way at least three sentences must use it.
 *
 * Two notes ride along because they are why It, There and This are on the watch list:
 * an expletive opener ("It is…", "There are…") that delays the real subject, and a
 * "This" with no noun after it ("This shows…"), which leaves the reader to work out
 * what "this" is.
 */

export type OpenerType = "transition" | "clause" | "phrase" | "subject";

export interface OpenerSentence {
  start: number;
  end: number;
  text: string;
  /** The opening word as written, and where it is. */
  word: string;
  wordStart: number;
  wordEnd: number;
  /** Lowercase key of the opening word. */
  key: string;
  /** The first two words as written ("However, the") and their key ("however the"). */
  twoWords: string;
  twoKey: string;
  type: OpenerType;
  /** In a run of three or more sentences opening with the same word. */
  inRun: boolean;
  /** Opens with a watched word that the text uses heavily. */
  heavy: boolean;
  /** "It is…", "There are…": the grammatical subject is a placeholder. */
  expletive: boolean;
  /** "This shows…": "This" with no noun after it. */
  bareThis: boolean;
}

export interface OpenerShare {
  key: string;
  /** As first written: "The", "In addition". */
  label: string;
  count: number;
  /** Share of analysed sentences, 0–1. */
  share: number;
}

export interface OpenerRun {
  key: string;
  label: string;
  /** Index of the first sentence of the run, in `sentences`. */
  first: number;
  length: number;
}

export interface OpenerReport {
  sentences: OpenerSentence[];
  /** Headings and lines without words are not analysed. */
  skipped: number;
  openers: OpenerShare[];
  twoWordOpeners: OpenerShare[];
  runs: OpenerRun[];
  heavy: OpenerShare[];
  types: Record<OpenerType, number>;
  expletives: number;
  bareThis: number;
}

export const RUN_LENGTH = 3;
export const HEAVY_MIN_COUNT = 3;

/** The watched openers and the share of sentences at which each counts as heavy. */
export const HEAVY_SHARES: Readonly<Record<string, number>> = {
  i: 0.2,
  it: 0.2,
  the: 0.3,
  there: 0.2,
  this: 0.2,
};

/** Multi-word transitions, matched on the first words of a sentence. */
const TRANSITION_PHRASES = [
  "above all",
  "after all",
  "all in all",
  "as a consequence",
  "as a result",
  "as such",
  "at the same time",
  "by comparison",
  "by contrast",
  "even so",
  "for example",
  "for instance",
  "in addition",
  "in comparison",
  "in conclusion",
  "in contrast",
  "in fact",
  "in other words",
  "in particular",
  "in short",
  "in sum",
  "in summary",
  "in turn",
  "on the contrary",
  "on the other hand",
  "taken together",
  "that is",
  "to illustrate",
  "to summarize",
  "to this end",
  "with this in mind",
].map((phrase) => phrase.split(" "));

const TRANSITION_WORDS = new Set([
  "accordingly",
  "additionally",
  "admittedly",
  "also",
  "alternatively",
  "besides",
  "consequently",
  "conversely",
  "correspondingly",
  "finally",
  "first",
  "firstly",
  "further",
  "furthermore",
  "hence",
  "however",
  "importantly",
  "indeed",
  "instead",
  "lastly",
  "likewise",
  "meanwhile",
  "moreover",
  "nevertheless",
  "next",
  "nonetheless",
  "notably",
  "otherwise",
  "overall",
  "second",
  "secondly",
  "similarly",
  "specifically",
  "still",
  "subsequently",
  "then",
  "therefore",
  "third",
  "thirdly",
  "thus",
  "ultimately",
]);

/** Openers that start an introductory clause: "Although…", "Because…", "To test this…". */
const CLAUSE_WORDS = new Set([
  "although",
  "as",
  "because",
  "given",
  "if",
  "once",
  "provided",
  "since",
  "though",
  "to",
  "unless",
  "when",
  "whenever",
  "where",
  "whereas",
  "wherever",
  "whether",
  "while",
  "whilst",
]);

/** Openers that start an introductory phrase: a preposition or a participle. */
const PHRASE_WORDS = new Set([
  "about",
  "according",
  "across",
  "after",
  "against",
  "along",
  "amid",
  "among",
  "at",
  "based",
  "before",
  "beyond",
  "building",
  "by",
  "compared",
  "considering",
  "despite",
  "drawing",
  "due",
  "during",
  "following",
  "for",
  "from",
  "having",
  "in",
  "inside",
  "like",
  "of",
  "on",
  "outside",
  "over",
  "regarding",
  "taking",
  "through",
  "throughout",
  "toward",
  "towards",
  "under",
  "unlike",
  "until",
  "upon",
  "using",
  "with",
  "within",
  "without",
]);

const EXPLETIVE_FOLLOWERS: Readonly<Record<string, ReadonlySet<string>>> = {
  it: new Set([
    "appears",
    "can",
    "could",
    "has",
    "is",
    "may",
    "might",
    "seems",
    "should",
    "was",
    "will",
    "would",
  ]),
  there: new Set([
    "appear",
    "appears",
    "are",
    "exist",
    "exists",
    "has",
    "have",
    "is",
    "remain",
    "remains",
    "seem",
    "seems",
    "was",
    "were",
    "will",
  ]),
};

/** Verbs (and modals) straight after "This" that show no noun is there. */
const VERBS_AFTER_BARE_THIS = new Set([
  "can",
  "confirms",
  "contradicts",
  "could",
  "demonstrates",
  "explains",
  "has",
  "highlights",
  "illustrates",
  "implies",
  "indicates",
  "is",
  "leads",
  "makes",
  "may",
  "means",
  "might",
  "raises",
  "reflects",
  "reveals",
  "shows",
  "suggests",
  "supports",
  "was",
  "will",
  "would",
]);

const RE_NON_WORD = /[^\p{L}\p{N}\s'’-]/gu;
const RE_SPACES = /\s+/g;

function keyOf(text: string): string {
  return text
    .toLowerCase()
    .replace(RE_NON_WORD, "")
    .replace(RE_SPACES, " ")
    .trim();
}

function typeOf(words: readonly string[]): OpenerType {
  const isPhraseTransition = TRANSITION_PHRASES.some((phrase) =>
    phrase.every((word, index) => words[index] === word)
  );
  const first = words[0] ?? "";
  if (isPhraseTransition || TRANSITION_WORDS.has(first)) {
    return "transition";
  }
  // "Even though…" and "Even if…" open clauses; "Even small changes…" is the subject.
  const evenClause =
    first === "even" && (words[1] === "though" || words[1] === "if");
  if (evenClause || CLAUSE_WORDS.has(first)) {
    return "clause";
  }
  return PHRASE_WORDS.has(first) ? "phrase" : "subject";
}

function describe(sentence: Sentence): OpenerSentence | null {
  const tokens = wordTokens(sentence.text);
  const firstToken = tokens[0];
  if (!firstToken) {
    return null;
  }
  const words = tokens.slice(0, 5).map((token) => token.lower);
  const secondToken = tokens[1];
  const twoWords = secondToken
    ? sentence.text.slice(firstToken.start, secondToken.end)
    : firstToken.text;
  const second = words[1] ?? "";
  return {
    start: sentence.start,
    end: sentence.end,
    text: sentence.text,
    word: firstToken.text,
    wordStart: sentence.start + firstToken.start,
    wordEnd: sentence.start + firstToken.end,
    key: words[0] ?? "",
    twoWords,
    twoKey: keyOf(twoWords),
    type: typeOf(words),
    inRun: false,
    heavy: false,
    expletive: EXPLETIVE_FOLLOWERS[words[0] ?? ""]?.has(second) ?? false,
    bareThis: words[0] === "this" && VERBS_AFTER_BARE_THIS.has(second),
  };
}

function shares(
  sentences: readonly OpenerSentence[],
  keyFor: (sentence: OpenerSentence) => string,
  labelFor: (sentence: OpenerSentence) => string
): OpenerShare[] {
  const rows = new Map<string, OpenerShare>();
  for (const sentence of sentences) {
    const key = keyFor(sentence);
    const row = rows.get(key);
    if (row) {
      row.count += 1;
    } else {
      rows.set(key, { key, label: labelFor(sentence), count: 1, share: 0 });
    }
  }
  const total = sentences.length;
  return [...rows.values()]
    .map((row) => ({ ...row, share: total > 0 ? row.count / total : 0 }))
    .sort((a, b) => b.count - a.count || a.key.localeCompare(b.key));
}

function findRuns(sentences: OpenerSentence[]): OpenerRun[] {
  const runs: OpenerRun[] = [];
  let first = 0;
  for (let index = 1; index <= sentences.length; index += 1) {
    const continues =
      index < sentences.length && sentences[index].key === sentences[first].key;
    if (continues) {
      continue;
    }
    const length = index - first;
    if (length >= RUN_LENGTH) {
      runs.push({
        key: sentences[first].key,
        label: sentences[first].word,
        first,
        length,
      });
      for (let member = first; member < index; member += 1) {
        sentences[member].inRun = true;
      }
    }
    first = index;
  }
  return runs;
}

function findHeavy(openers: OpenerShare[]): OpenerShare[] {
  return openers.filter((row) => {
    const threshold = HEAVY_SHARES[row.key];
    return (
      threshold !== undefined &&
      row.count >= HEAVY_MIN_COUNT &&
      row.share >= threshold
    );
  });
}

export function analyzeSentenceOpeners(text: string): OpenerReport {
  const all = splitSentences(text);
  const headings = headingFlags(all);
  const sentences = all
    .filter((_, index) => !headings[index])
    .map(describe)
    .filter((sentence): sentence is OpenerSentence => sentence !== null);
  const openers = shares(
    sentences,
    (s) => s.key,
    (s) => s.word
  );
  const runs = findRuns(sentences);
  const heavy = findHeavy(openers);
  const heavyKeys = new Set(heavy.map((row) => row.key));
  const types: Record<OpenerType, number> = {
    transition: 0,
    clause: 0,
    phrase: 0,
    subject: 0,
  };
  for (const sentence of sentences) {
    sentence.heavy = heavyKeys.has(sentence.key);
    types[sentence.type] += 1;
  }
  return {
    sentences,
    skipped: all.length - sentences.length,
    openers,
    twoWordOpeners: shares(
      sentences,
      (s) => s.twoKey,
      (s) => s.twoWords
    ),
    runs,
    heavy,
    types,
    expletives: sentences.filter((s) => s.expletive).length,
    bareThis: sentences.filter((s) => s.bareThis).length,
  };
}
